/**
 * Tipos TypeScript para o Sistema de Galeria de Mídia
 * 
 * Sistema permite que tenants façam upload e gerenciem arquivos
 * (imagens, vídeos, áudios, documentos) para uso em fluxos de automação.
 * Limite: 200MB por tenant
 */

import { Database } from '@/integrations/supabase/types';

// Tipo base da tabela media_gallery
export type MediaGalleryRow = Database['public']['Tables']['media_gallery']['Row'];
export type MediaGalleryInsert = Database['public']['Tables']['media_gallery']['Insert'];
export type MediaGalleryUpdate = Database['public']['Tables']['media_gallery']['Update'];

/**
 * Tipos de arquivo suportados pela galeria
 */
export type MediaFileType = 'image' | 'video' | 'audio' | 'document';

/**
 * Interface completa de um item da galeria de mídia
 */
export interface MediaGalleryItem {
  id: string;
  tenant_id: string;
  name: string;
  file_type: MediaFileType;
  mime_type: string;
  storage_path: string;
  size_bytes: number;
  thumbnail_url?: string;
  metadata: MediaMetadata;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Metadados específicos por tipo de mídia
 */
export interface MediaMetadata {
  // Para imagens
  width?: number;
  height?: number;
  
  // Para vídeos e áudios
  duration?: number; // em segundos
  
  // Para todos
  original_filename?: string;
  file_extension?: string;
  [key: string]: any; // Permite campos adicionais
}

/**
 * Filtros para busca na galeria
 */
export interface MediaGalleryFilters {
  file_type?: MediaFileType | 'all';
  search?: string; // Busca por nome
  uploaded_by?: string;
  date_from?: Date;
  date_to?: Date;
}

/**
 * Limites de tamanho por tipo de arquivo (em bytes)
 */
export const MEDIA_SIZE_LIMITS = {
  image: 10 * 1024 * 1024,      // 10MB
  video: 50 * 1024 * 1024,      // 50MB
  audio: 20 * 1024 * 1024,      // 20MB
  document: 20 * 1024 * 1024,   // 20MB
} as const;

/**
 * Limite total de armazenamento por tenant (em bytes)
 */
export const TENANT_STORAGE_LIMIT = 200 * 1024 * 1024; // 200MB

/**
 * Tipos MIME aceitos por categoria
 */
export const ACCEPTED_MIME_TYPES: Record<MediaFileType, string[]> = {
  image: [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
  ],
  video: [
    'video/mp4',
    'video/webm',
    'video/quicktime', // .mov
  ],
  audio: [
    'audio/mpeg',      // .mp3
    'audio/ogg',
    'audio/wav',
    'audio/mp4',       // .m4a
  ],
  document: [
    'application/pdf',
    'application/msword',                                                  // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel',                                            // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',  // .xlsx
  ],
};

/**
 * Extensões de arquivo aceitas por categoria
 */
export const ACCEPTED_FILE_EXTENSIONS: Record<MediaFileType, string[]> = {
  image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  video: ['.mp4', '.webm', '.mov'],
  audio: ['.mp3', '.ogg', '.wav', '.m4a'],
  document: ['.pdf', '.doc', '.docx', '.xls', '.xlsx'],
};

/**
 * Interface para resultado do upload
 */
export interface MediaUploadResult {
  success: boolean;
  media?: MediaGalleryItem;
  error?: string;
}

/**
 * Interface para validação de arquivo
 */
export interface FileValidationResult {
  valid: boolean;
  error?: string;
  file_type?: MediaFileType;
}

/**
 * Interface para estatísticas de uso da galeria
 */
export interface MediaGalleryUsage {
  total_bytes: number;
  total_files: number;
  by_type: {
    image: { count: number; bytes: number };
    video: { count: number; bytes: number };
    audio: { count: number; bytes: number };
    document: { count: number; bytes: number };
  };
  percentage_used: number;
  remaining_bytes: number;
}

/**
 * Props para componentes de seleção de mídia
 */
export interface MediaPickerProps {
  onSelect: (media: MediaGalleryItem) => void;
  onCancel: () => void;
  allowedTypes?: MediaFileType[];
  currentSelection?: string; // ID da mídia selecionada atualmente
}

/**
 * Props para componente de galeria completa
 */
export interface MediaGalleryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectionMode?: boolean; // Se true, permite seleção de arquivos
  onSelect?: (media: MediaGalleryItem) => void;
}

/**
 * Função helper para determinar tipo de arquivo baseado em MIME type
 */
export function getFileTypeFromMime(mimeType: string): MediaFileType | null {
  for (const [type, mimes] of Object.entries(ACCEPTED_MIME_TYPES)) {
    if (mimes.includes(mimeType)) {
      return type as MediaFileType;
    }
  }
  return null;
}

/**
 * Função helper para validar tamanho de arquivo
 */
export function validateFileSize(fileType: MediaFileType, sizeBytes: number): boolean {
  return sizeBytes <= MEDIA_SIZE_LIMITS[fileType];
}

/**
 * Função helper para formatar bytes em formato legível
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Função helper para obter ícone baseado no tipo de arquivo
 */
export function getFileTypeIcon(fileType: MediaFileType): string {
  const icons: Record<MediaFileType, string> = {
    image: '🖼️',
    video: '🎥',
    audio: '🎵',
    document: '📄',
  };
  return icons[fileType] || '📁';
}

/**
 * Função helper para obter cor baseada no tipo de arquivo
 */
export function getFileTypeColor(fileType: MediaFileType): string {
  const colors: Record<MediaFileType, string> = {
    image: 'text-blue-500',
    video: 'text-purple-500',
    audio: 'text-green-500',
    document: 'text-orange-500',
  };
  return colors[fileType] || 'text-gray-500';
}
